import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useBusinessUnits } from '../../contexts/BusinessUnitsContext';
import useCurrentUser from '../../hooks/useCurrentUser';
import { useScrapeCarsMutation } from '../../services/api/setupOnboarding.api';
import { OnboardingWebsiteAnalysis, CarLinkConfig, ScrapeCarsByLinksResult } from '../../services/types/onboarding';
import styles from './SetupOnboarding.module.scss';
import { validateUrl, addHTTPSProtocolToUrl } from './utils';

interface ConfiguredLink {
  id: string;
  url: string;
  text: string;
  type: 'new' | 'used';
}

interface EditLinkDialogProps {
  open: boolean;
  link: ConfiguredLink | null;
  onClose: () => void;
  onSave: (link: ConfiguredLink) => void;
}

const EditLinkDialog: React.FC<EditLinkDialogProps> = ({ open, link, onClose, onSave }) => {
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [type, setType] = useState<'new' | 'used'>('new');
  const [errors, setErrors] = useState<{ url?: string; text?: string }>({});

  useEffect(() => {
    if (link) {
      setUrl(link.url);
      setText(link.text);
      setType(link.type);
    } else {
      setUrl('');
      setText('');
      setType('new');
    }
    setErrors({});
  }, [link]);

  const handleSave = () => {
    setErrors({});

    const normalizedUrl = addHTTPSProtocolToUrl(url.trim());

    if (!normalizedUrl) {
      setErrors({ url: 'La URL es requerida' });
      return;
    }

    if (!validateUrl(normalizedUrl)) {
      setErrors({ url: 'Por favor ingresa una URL válida' });
      return;
    }

    if (!text.trim()) {
      setErrors({ text: 'El texto descriptivo es requerido' });
      return;
    }

    const updatedLink: ConfiguredLink = {
      id: link?.id || `link-${Date.now()}`,
      url: normalizedUrl,
      text: text.trim(),
      type,
    };

    onSave(updatedLink);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{link ? 'Editar Enlace' : 'Añadir Nuevo Enlace'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} pt={1}>
          <TextField
            fullWidth
            label="URL del Enlace"
            placeholder="https://concesionario.com/autos-nuevos"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={!!errors.url}
            helperText={errors.url || 'URL completa al catálogo de vehículos'}
            InputProps={{
              startAdornment: <LinkIcon color="action" style={{ marginRight: 8 }} />,
            }}
          />

          <TextField
            fullWidth
            label="Texto Descriptivo"
            placeholder="Ej: Catálogo de Autos Nuevos"
            value={text}
            onChange={(e) => setText(e.target.value)}
            error={!!errors.text}
            helperText={errors.text || 'Descripción del enlace para referencia'}
          />

          <FormControl fullWidth>
            <InputLabel>Tipo de Vehículos</InputLabel>
            <Select value={type} label="Tipo de Vehículos" onChange={(e) => setType(e.target.value as 'new' | 'used')}>
              <MenuItem value="new">Autos Nuevos (0 KM)</MenuItem>
              <MenuItem value="used">Autos Usados/Seminuevos</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
          {link ? 'Guardar Cambios' : 'Añadir Enlace'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ConfigureLinks: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [configuredLinks, setConfiguredLinks] = useState<ConfiguredLink[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<ConfiguredLink | null>(null);
  const [scrapingResults, setScrapingResults] = useState<ScrapeCarsByLinksResult | null>(null);

  const [scrapeCars, { isLoading: isScrapingCars }] = useScrapeCarsMutation();

  const currentUser = useCurrentUser();
  const { getBusinessUnitById } = useBusinessUnits();

  const currentBusinessUnit = currentUser?.businessUnitIds?.[0]
    ? getBusinessUnitById(currentUser.businessUnitIds[0])
    : null;

  const analysisResult = (location.state as { analysisResult?: OnboardingWebsiteAnalysis } | null)?.analysisResult;

  useEffect(() => {
    if (!analysisResult) {
      navigate('/setup-onboarding');
      return;
    }

    const allLinks: ConfiguredLink[] = [
      ...analysisResult.vehicleLinks.newCarsUrls.map((url, index) => ({
        id: `new-${index}`,
        url,
        text: analysisResult.vehicleLinks.newCarsTexts[index] || 'Autos Nuevos',
        type: 'new' as const,
      })),

      ...analysisResult.vehicleLinks.usedCarsUrls.map((url, index) => ({
        id: `used-${index}`,
        url,
        text: analysisResult.vehicleLinks.usedCarsTexts[index] || 'Autos Usados',
        type: 'used' as const,
      })),
      // Enlaces específicos por marca (se consideran autos nuevos)
      ...analysisResult.vehicleLinks.brandSpecificUrls.map((brandUrl, index) => ({
        id: `brand-${index}`,
        url: brandUrl.url,
        text: `${brandUrl.brand} - ${brandUrl.text}`,
        type: 'new' as const,
      })),
    ];

    const uniqueLinks = allLinks.filter((link, index, self) => index === self.findIndex((l) => l.url === link.url));

    setConfiguredLinks(uniqueLinks);
  }, [analysisResult, navigate]);

  const handleEditLink = (link: ConfiguredLink) => {
    setEditingLink(link);
    setEditDialogOpen(true);
  };

  const handleAddLink = () => {
    setEditingLink(null);
    setEditDialogOpen(true);
  };

  const handleSaveLink = (link: ConfiguredLink) => {
    if (editingLink) {
      // Actualizar enlace existente
      setConfiguredLinks((prev) => prev.map((l) => (l.id === editingLink.id ? link : l)));
    } else {
      // Añadir nuevo enlace
      setConfiguredLinks((prev) => [...prev, link]);
    }
  };

  const handleDeleteLink = (linkId: string) => {
    setConfiguredLinks((prev) => prev.filter((l) => l.id !== linkId));
  };

  const handleContinue = async () => {
    if (configuredLinks.length === 0) {
      return;
    }

    try {
      // Convertir ConfiguredLink[] a CarLinkConfig[]
      const linksToScrape: CarLinkConfig[] = configuredLinks.map((link) => ({
        id: link.id,
        url: link.url,
        text: link.text,
        type: link.type,
      }));

      // Hacer scraping de los enlaces
      const response = await scrapeCars({ links: linksToScrape }).unwrap();

      if (response.success) {
        setScrapingResults(response.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error al hacer scraping:', error);
      // eslint-disable-next-line no-alert
      alert('Error al procesar los enlaces. Por favor, inténtalo de nuevo.');
    }
  };

  const handleBack = () => {
    navigate('/setup-onboarding', {
      state: { analysisResult },
    });
  };

  const newCarLinks = configuredLinks.filter((link) => link.type === 'new');
  const usedCarLinks = configuredLinks.filter((link) => link.type === 'used');

  if (!analysisResult) {
    return null;
  }

  return (
    <div className={styles.setupContainer}>
      <div className={styles.setupContent}>
        {/* Header */}
        <Box className={styles.header}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
            className={styles.backButton}
            sx={{ alignSelf: 'flex-start', mb: 2 }}
          >
            Volver Atrás
          </Button>

          <DirectionsCarIcon className={styles.headerIcon} />
          <Typography variant="h4" component="h1" className={styles.title}>
            Configurar Enlaces de Inventario
          </Typography>
          <Typography variant="subtitle1" className={styles.subtitle}>
            Revisa y configura los enlaces para hacer scraping de tu inventario
          </Typography>
        </Box>

        {/* Progress indicator */}
        <Box className={styles.progressSection}>
          <Typography variant="body2" className={styles.progressLabel}>
            Paso 2 de 3
          </Typography>
          <LinearProgress variant="determinate" value={66} className={styles.progressBar} />
        </Box>

        {/* Summary Card */}
        <Card className={styles.setupCard}>
          <CardContent className={styles.cardContent}>
            <Typography variant="h6" component="h2" gutterBottom>
              Resumen de Enlaces Detectados
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Hemos detectado automáticamente {configuredLinks.length} enlaces de inventario en tu sitio web. Puedes
              agregar más enlaces manualmente o eliminar los que no desees usar.
            </Typography>
            <Box display="flex" gap={2} mb={2}>
              <Chip label={`${newCarLinks.length} Autos Nuevos`} color="primary" variant="outlined" />
              <Chip label={`${usedCarLinks.length} Autos Usados`} color="secondary" variant="outlined" />
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddLink} sx={{ mt: 1 }}>
              Añadir Enlace Manualmente
            </Button>
          </CardContent>
        </Card>

        {/* New Cars Links */}
        {newCarLinks.length > 0 && (
          <Card className={styles.setupCard}>
            <CardContent className={styles.cardContent}>
              <Typography variant="h6" component="h2" color="primary" gutterBottom>
                Enlaces de Autos Nuevos ({newCarLinks.length})
              </Typography>
              {newCarLinks.map((link) => (
                <Box
                  key={link.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                  mb={1}
                  border={1}
                  borderColor="divider"
                  borderRadius={1}
                >
                  <Box flex={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      {link.text}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="primary.main"
                      component="a"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'none' }}
                    >
                      {link.url}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <IconButton size="small" onClick={() => handleEditLink(link)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteLink(link.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Used Cars Links */}
        {usedCarLinks.length > 0 && (
          <Card className={styles.setupCard}>
            <CardContent className={styles.cardContent}>
              <Typography variant="h6" component="h2" color="secondary" gutterBottom>
                Enlaces de Autos Usados ({usedCarLinks.length})
              </Typography>
              {usedCarLinks.map((link) => (
                <Box
                  key={link.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                  mb={1}
                  border={1}
                  borderColor="divider"
                  borderRadius={1}
                >
                  <Box flex={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      {link.text}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="secondary.main"
                      component="a"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'none' }}
                    >
                      {link.url}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <IconButton size="small" onClick={() => handleEditLink(link)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteLink(link.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {configuredLinks.length === 0 && (
          <Card className={styles.setupCard}>
            <CardContent className={styles.cardContent} sx={{ textAlign: 'center', py: 4 }}>
              <LinkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No hay enlaces configurados
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                No se detectaron enlaces automáticamente. Puedes añadir enlaces manualmente.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddLink}>
                Añadir Primer Enlace
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Scraping Progress */}
        {isScrapingCars && (
          <Card className={styles.setupCard}>
            <CardContent className={styles.cardContent} sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Procesando Enlaces de Inventario
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Estamos analizando {configuredLinks.length} enlaces para extraer información de los vehículos. Este
                proceso puede tomar unos minutos...
              </Typography>
              <LinearProgress sx={{ mt: 2, width: '100%' }} />
            </CardContent>
          </Card>
        )}

        {/* Scraping Results */}
        {scrapingResults && !isScrapingCars && (
          <Card className={styles.setupCard}>
            <CardContent className={styles.cardContent} sx={{ textAlign: 'center', py: 3 }}>
              <DirectionsCarIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom color="success.main">
                ¡Inventario Actualizado Exitosamente!
              </Typography>
              <Typography variant="body1" paragraph>
                Se encontraron <strong>{scrapingResults.newCars.length}</strong> autos nuevos y{' '}
                <strong>{scrapingResults.usedCars.length}</strong> autos usados
              </Typography>
              {scrapingResults.errors.length > 0 && (
                <Typography variant="body2" color="warning.main">
                  {scrapingResults.errors.length} enlaces tuvieron errores durante el procesamiento
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <Box display="flex" justifyContent="center" mt={4}>
          {scrapingResults && !isScrapingCars ? (
            <Button
              variant="contained"
              size="large"
              endIcon={<DirectionsCarIcon />}
              onClick={() => {
                const pusherIdentification = currentBusinessUnit?.pusherIdentification;
                if (pusherIdentification) {
                  navigate(`/demochat/${pusherIdentification}/newchat`);
                } else {
                  navigate('/dashboard');
                }
              }}
            >
              Prueba la Experiencia
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard')}
                disabled={isScrapingCars}
                sx={{ mr: 2 }}
              >
                Saltar por Ahora
              </Button>

              <Button
                variant="contained"
                size="large"
                endIcon={isScrapingCars ? <CircularProgress size={20} color="inherit" /> : <NavigateNextIcon />}
                onClick={() => {
                  // eslint-disable-next-line
                  handleContinue();
                }}
                disabled={configuredLinks.length === 0 || isScrapingCars}
              >
                {isScrapingCars ? 'Procesando Enlaces...' : 'Actualizar Inventario'}
              </Button>
            </>
          )}
        </Box>

        {/* Edit Dialog */}
        <EditLinkDialog
          open={editDialogOpen}
          link={editingLink}
          onClose={() => setEditDialogOpen(false)}
          onSave={handleSaveLink}
        />
      </div>
    </div>
  );
};

export default ConfigureLinks;
